import {
  Plus,
  Minus,
  Trash2,
  X,
  Check,
  CreditCard,
  Banknote,
  DollarSign,
  Truck,
  User,
  Users,
  AlertCircle,
  Key,
  LogOut,
  Printer,
  FileText,
  Calendar,
  Monitor,
  CheckSquare,
  LogIn,
  Phone,
  MapPin,
  Loader2,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import React, { useState, useEffect, useRef, useCallback } from 'react'

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-red-600 bg-red-50 rounded-lg m-4 border border-red-200">
          <h1 className="text-xl font-bold mb-4">¡Ocurrió un error en la interfaz!</h1>
          <p className="mb-2">Por favor toma captura de este error para que el equipo de desarrollo lo revise:</p>
          <pre className="p-4 bg-red-100 rounded text-sm overflow-auto max-w-full">
            {this.state.error?.message}
            <br /><br />
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useHotkeys } from 'react-hotkeys-hook'
import { useQueuedCartQuantityUpdates } from '../hooks/useQueuedCartQuantityUpdates'
import { ProductCard } from '../components/pos/ProductCard'
import { ProductDiscoveryControls } from '../components/pos/ProductDiscoveryControls'
import { PosMobileTabs, type PosMobilePanel } from '../components/pos/PosMobileTabs'
import { ShortcutsWidget } from '../components/pos/ShortcutsWidget'

import { PosCart } from '../components/pos/PosCart'


import { CashCountModal, CashCountResultModal } from '../components/pos/modals/CashCountModal'
import { CashDepositModal, type CashDepositTarget } from '../components/pos/modals/CashDepositModal'
import { depositStoreOrderPickupCash, remitStoreOrderCash } from '../services/store-orders.service'
import { CashWithdrawalModal } from '../components/pos/modals/CashWithdrawalModal'
import { ChangeCashierModal } from '../components/pos/modals/ChangeCashierModal'
import { ChangeSellerModal } from '../components/pos/modals/ChangeSellerModal'
import { DeliveryDetailsModal } from '../components/pos/modals/DeliveryDetailsModal'
import { NewCustomerModal } from '../components/pos/modals/NewCustomerModal'
import { PaymentModal, SuccessModal } from '../components/pos/modals/PaymentModal'
import { PrescriptionModal } from '../components/pos/modals/PrescriptionModal'
import { TicketPreviewModal } from '../components/pos/modals/TicketPreviewModal'
import { RegisterAssignmentModal } from '../components/pos/RegisterAssignmentModal'
import { OpenRegisterSessionModal } from '../components/pos/modals/OpenRegisterSessionModal'
import { InvoiceDataModal, InvoiceData } from '../components/pos/modals/InvoiceDataModal'
import { PriceCheckModal } from '../components/pos/modals/PriceCheckModal'
import { WbsRechargeModal } from '../components/pos/modals/WbsRechargeModal'
import { InventorySourceModal, type InventorySelectionResult } from '../components/pos/modals/InventorySourceModal'
import { SaleDetailModal } from '../components/sales/modals/SaleDetailModal'
import { coordinateControlledInventory, persistControlledInventorySelection, type ControlledInventorySelection } from '../components/pos/inventory-selection'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover'

import {
  Product,
  CartItem,
  DeliveryDetails,

  posCustomerService,
  posRegisterService,
  posCartService,
  posSessionService,
  posCashMovementService,
  posSalesService,
  PosCartData,
  cartFromValidationError,
  posCartRevision,
  posCartExpirationDelay,
  posCartRefetchInterval,
} from '../services/pos.service'
import { facturamaService } from '../services/facturama.service'
import { paymentMethodService, type PaymentMethodItem } from '../services/payment-method.service'
import { salesService } from '../services/sales.service'
import { usePosLoyaltyQuote } from '../hooks/usePosLoyaltyQuote'
import { usePosProductCatalog } from '../hooks/usePosProductCatalog'
import { useSessionStore } from '../store/auth/auth.store'


export function PointOfSale() {
  const [mobilePanel, setMobilePanel] = useState<PosMobilePanel>('products')
  const [selectedUseCase, setSelectedUseCase] = useState('all')
  const [customers, setCustomers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('')
  const autoAddInFlightRef = useRef<string | null>(null)
  const lastAutoAddedSearchRef = useRef<string | null>(null)
  const [inventorySelection, setInventorySelection] = useState<ControlledInventorySelection | null>(null)
  const canAction = useSessionStore((s) => s.canAction)
  console.log('WBS DEBUG:', canAction('pos.pos.wbs'))
  const isAdministrator = useSessionStore((s) => s.user?.role?.code === 1)
  const canAssignSeller = isAdministrator && canAction('pos.seller.assign')

  const reloadCustomers = async () => {
    if (!canAction('clients.customer.view') && !canAction('pos.customer.create')) return
    try {
      const customersData = await posCustomerService.getCustomers();
      setCustomers(customersData);
    } catch (error) {
      console.error("Error recargando clientes", error);
    }
  };

  useEffect(() => {
    const loadCatalogs = async () => {
      if (!canAction('clients.customer.view') && !canAction('pos.customer.create')) return
      try {
        const [customersData] = await Promise.all([
          posCustomerService.getCustomers(),
        ]);
        setCustomers(customersData);
      } catch (error) {
        console.error("Error cargando datos", error);
      }
    };
    void loadCatalogs();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      lastAutoAddedSearchRef.current = null
    }
  }, [searchTerm])

  const {
    data: registerContext,
    isLoading: isLoadingContext,
    refetch: refetchContext,
  } = useQuery({
    queryKey: ['register-context'],
    queryFn: posRegisterService.getRegisterContext,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  })
  console.log('REGISTER CONTEXT:', registerContext)

  const { data: facturamaProfile } = useQuery({
    queryKey: ['facturama-profile'],
    queryFn: facturamaService.getProfile,
    staleTime: 1000 * 60 * 60 * 2, // 2 horas
    enabled: canAction('pos.sale.invoice'),
  })

  const { data: paymentMethods = [] } = useQuery<PaymentMethodItem[]>({
    queryKey: ['payment-methods', 'pos'],
    queryFn: async () => {
      const response = await paymentMethodService.getItems({ all: true })
      const items = response.data?.data || response.data || []

      return Array.isArray(items) ? items : []
    },
    staleTime: 1000 * 60 * 5,
  })

  // Modales de contexto
  const [showRegisterAssignment, setShowRegisterAssignment] = useState(false)
  const [showOpenSession, setShowOpenSession] = useState(false)
  const [showPriceCheckModal, setShowPriceCheckModal] = useState(false)
  const [addedProductNotice, setAddedProductNotice] = useState<{ name: string; sequence: number } | null>(null)
  const [showWbsModal, setShowWbsModal] = useState(false)
  const queryClient = useQueryClient()
  const {
    products,
    isLoading: isLoadingProducts,
    isRefreshing: isRefreshingProducts,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refresh: refreshProducts,
  } = usePosProductCatalog({
    search: searchTerm,
    useCaseId: selectedUseCase,
    sucursalId: registerContext?.sucursal?.id ?? null,
  })
  const isLoadingData = isLoadingProducts || isRefreshingProducts || isFetchingNextPage

  const { data: latestSaleData } = useQuery({
    queryKey: [
      'pos-last-completed-sale',
      registerContext?.sucursal?.id,
      registerContext?.cash_register?.id,
      registerContext?.session?.id,
    ],
    queryFn: () => salesService.getSales({
      per_page: 1,
      sucursal_id: registerContext!.sucursal.id,
      cash_register_id: registerContext!.cash_register!.id,
      cash_register_session_id: registerContext!.session!.id,
      status: 'COMPLETED',
    }),
    enabled: Boolean(
      canAction('pos.sale.print')
      && registerContext?.can_sell
      && registerContext.cash_register?.id
      && registerContext.session?.id,
    ),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  })
  const lastSaleId = latestSaleData?.data[0]?.id ?? null

  const openControlledProduct = useCallback((product: Product, increment = 1, searchKey?: string) => {
    const currentCart = queryClient.getQueryData<PosCartData>(['pos-cart'])
    setInventorySelection(coordinateControlledInventory(product, currentCart, increment, searchKey))
  }, [queryClient])

  const { data: cartData, isLoading: isLoadingCart } = useQuery({
    queryKey: ['pos-cart'],
    queryFn: posCartService.getCart,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const currentCart = query.state.data as PosCartData | undefined

      return posCartRefetchInterval(currentCart)
    },
    refetchIntervalInBackground: false,
  })

  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([])
  const cart = (cartData?.items || []).map((item: CartItem) => ({
    ...item,
    selected: selectedItemIds.includes(item.id)
  }))
  const cartQuantity = cart.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0,
  )

  const syncCart = useCallback((nextCart: PosCartData) => {
    queryClient.setQueryData(['pos-cart'], nextCart)
  }, [queryClient])

  const announceProductAdded = (productName: string) => {
    setAddedProductNotice((current) => ({
      name: productName,
      sequence: (current?.sequence ?? 0) + 1,
    }))
  }
  const cartRevision = posCartRevision(cartData)
  const previousCartRevisionRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (cartRevision === null) return

    if (previousCartRevisionRef.current === undefined) {
      previousCartRevisionRef.current = cartRevision
      return
    }

    if (previousCartRevisionRef.current !== cartRevision) {
      previousCartRevisionRef.current = cartRevision
      void refreshProducts()
    }
  }, [cartRevision, refreshProducts])

  useEffect(() => {
    const expirationDelay = posCartExpirationDelay(cartData)

    if (expirationDelay === null) return

    const timeout = window.setTimeout(
      () => queryClient.invalidateQueries({ queryKey: ['pos-cart'] }),
      expirationDelay,
    )

    return () => window.clearTimeout(timeout)
  }, [cartData, queryClient])

  const addItemMutation = useMutation({
    mutationFn: (variables: { product_id: number; quantity: number; productName: string }) => (
      posCartService.createCartItem({
        product_id: variables.product_id,
        quantity: variables.quantity,
      })
    ),
    onSuccess: (nextCart, variables) => {
      syncCart(nextCart)
      announceProductAdded(variables.productName)
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al agregar producto')
  })

  const removeItemMutation = useMutation({
    mutationFn: posCartService.deleteCartItem,
    onSuccess: syncCart,
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al eliminar producto')
  })

  const handleQuantityUpdateError = useCallback((err: any) => {
    toast.error(err?.response?.data?.message || 'Error al actualizar cantidad')
  }, [])
  const quantityUpdates = useQueuedCartQuantityUpdates({
    cart: cartData,
    client: posCartService,
    onCartChange: syncCart,
    onError: handleQuantityUpdateError,
  })

  const closeSessionMutation = useMutation({
    mutationFn: posSessionService.updateSession,
    onSuccess: (data) => {
      setClosedSessionData(data)
      toast.success('Sesión de caja cerrada correctamente.')
      queryClient.invalidateQueries({ queryKey: ['register-context'] })

      // Compute status for the result modal
      const counted = parseFloat(countedCash || '0')
      const difference = counted - currentCashInRegister
      setCashCountDifference(difference)

      if (difference === 0) {
        setCashCountStatus('success')
      } else if (difference > 0) {
        setCashCountStatus('surplus')
      } else {
        setCashCountStatus('shortage')
      }

      setShowCashCount(false)
      setShowCashCountResult(true)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al cerrar la caja'
      toast.error(msg)
    }
  })

  const depositMutation = useMutation({
    mutationFn: posCashMovementService.createDeposit,
    onSuccess: () => {
      toast.success('Depósito registrado correctamente.')
      refetchContext()
      setDepositAmount('')
      setDepositConcept('')
      setShowCashDeposit(false)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al registrar depósito'
      toast.error(msg)
    }
  })

  const orderDepositMutation = useMutation({
    mutationFn: async (target: Exclude<CashDepositTarget, { type: 'general' }>) => {
      if (target.type === 'pickup') {
        return depositStoreOrderPickupCash(target.orderNumber, depositAmount)
      }
      return remitStoreOrderCash(target.collectionId, depositAmount)
    },
    onSuccess: () => {
      toast.success('Ingreso vinculado al pedido registrado correctamente.')
      refetchContext()
      queryClient.invalidateQueries({ queryKey: ['cash-deposit-pickup-orders'] })
      queryClient.invalidateQueries({ queryKey: ['cash-deposit-collections'] })
      setDepositAmount('')
      setDepositConcept('')
      setShowCashDeposit(false)
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'No se pudo registrar el ingreso del pedido'),
  })

  const withdrawalMutation = useMutation({
    mutationFn: posCashMovementService.createWithdrawal,
    onSuccess: () => {
      toast.success('Retiro registrado correctamente.')
      refetchContext()
      setWithdrawalAmount('')
      setWithdrawalConcept('')
      setShowCashWithdrawal(false)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al registrar retiro'
      toast.error(msg)
    }
  })

  const clearCartMutation = useMutation({
    mutationFn: posCartService.clearCart,
    onSuccess: syncCart,
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al vaciar el carrito')
  })

  const releaseMutation = useMutation({
    mutationFn: (pin: string) => {
      const registerId = registerContext?.cash_register?.id
      if (!registerId) throw new Error('No hay una caja asignada.')
      return posRegisterService.releaseRegisterAssignment(registerId, pin)
    },
    onSuccess: () => {
      toast.success('Caja liberada correctamente')
      refetchContext()
      setReleasePin('')
      setShowChangeCashier(false)
    },
    onError: (err: any) => {
      setReleasePin('')
      toast.error(err?.response?.data?.message || 'Error al liberar la caja')
    }
  })

  const validateCartMutation = useMutation({
    mutationFn: posCartService.validateCart,
    onSuccess: syncCart,
    onError: (err: any) => {
      const recalculatedCart = cartFromValidationError(err)

      if (recalculatedCart) syncCart(recalculatedCart)
      toast.error(err?.response?.data?.message || 'Error al validar el carrito')
    },
  })

  const processSaleMutation = useMutation({
    mutationFn: posSalesService.createSale,
    onSuccess: (data) => {
      // Data contains the processed sale and ticket information
      queryClient.invalidateQueries({ queryKey: ['pos-cart'] })
      queryClient.invalidateQueries({ queryKey: ['pos-cash-movements'] })
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-suggested-orders'] })
      refetchContext()
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al procesar la venta')
  })
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showTicketPreview, setShowTicketPreview] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [completedSaleTotal, setCompletedSaleTotal] = useState(0)
  const [showCashWithdrawal, setShowCashWithdrawal] = useState(false)
  const [showCashCount, setShowCashCount] = useState(false)
  const [showCashCountResult, setShowCashCountResult] = useState(false)
  const [showChangeCashier, setShowChangeCashier] = useState(false)
  const [showChangeSeller, setShowChangeSeller] = useState(false)
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false)
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false)
  const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] =
    useState(false)
  const [showCashDeposit, setShowCashDeposit] = useState(false)
  const [showInvoiceDataModal, setShowInvoiceDataModal] = useState(false)
  const [closedSessionData, setClosedSessionData] = useState<any>(null)

  // Last sale reprint
  const [showLastSalePopover, setShowLastSalePopover] = useState(false)
  const [lastSaleCopies, setLastSaleCopies] = useState(1)
  const [showLastSalePrint, setShowLastSalePrint] = useState(false)

  // Invoice status
  const [invoiceStatus, setInvoiceStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  const [cfdiPdfBase64, setCfdiPdfBase64] = useState<string | null>(null)
  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    rfc: '',
    name: '',
    cfdi_use: '',
    fiscal_regime: '',
    zip_code: '',
    email: '',
  })

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null)
  const [cashReceived, setCashReceived] = useState('')
  const [currentCashInRegister, setCurrentCashInRegister] = useState(2500)
  const [selectedBank, setSelectedBank] = useState<any>(null)
  const [usePoints, setUsePoints] = useState(false)
  const [reference, setReference] = useState('')
  const [releasePin, setReleasePin] = useState('')

  // Sale type
  const [saleType, setSaleType] = useState<'regular' | 'delivery'>('regular')
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState('')
  const [deliveryNotes, setDeliveryNotes] = useState('')

  // Customer
  const [customerType, setCustomerType] = useState<'general' | 'registered'>(
    'general',
  )
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null)

  // Invoice
  const [requiresInvoice, setRequiresInvoice] = useState(false)
  const [ticketCopies, setTicketCopies] = useState(1)



  // Cash count
  const [countedCash, setCountedCash] = useState('')
  const [denominations, setDenominations] = useState({
    1: 0,
    2: 0,
    5: 0,
    10: 0,
    20: 0,
    50: 0,
    100: 0,
    200: 0,
    500: 0,
    1000: 0,
  })
  const [withdrawalAmount, setWithdrawalAmount] = useState('')
  const [withdrawalConcept, setWithdrawalConcept] = useState('')
  const [depositAmount, setDepositAmount] = useState('')
  const [depositConcept, setDepositConcept] = useState('')
  const [cashCountDifference, setCashCountDifference] = useState(0)
  const [cashCountNotes, setCashCountNotes] = useState('')
  const [cashCountStatus, setCashCountStatus] = useState<
    'success' | 'surplus' | 'shortage'
  >('success')

  useEffect(() => {
    if (showCashCount) {
      setCountedCash('')
      setCashCountNotes('')
      setDenominations({
        1: 0, 2: 0, 5: 0, 10: 0, 20: 0, 50: 0, 100: 0, 200: 0, 500: 0, 1000: 0,
      })
    }
  }, [showCashCount])
  // Delivery details
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDetails>({
    deliveryUserId: null,
    deliveryPerson: '',
    recipientName: '',
    customerPhone: '',
    customerAddress: '',
    observations: '',
    deliveryCost: 0,
  })

  // Ticket info
  const [ticketNumber, setTicketNumber] = useState('')
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)
  useEffect(() => {
    const handleAltPress = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', handleAltPress)
    window.addEventListener('keyup', handleAltPress)
    return () => {
      window.removeEventListener('keydown', handleAltPress)
      window.removeEventListener('keyup', handleAltPress)
    }
  }, [])

  const sellerBlockerMessage = registerContext?.sale_blocker_code === 'SELLER_INVALID'
    ? 'El vendedor asignado ya no está disponible. Un administrador debe seleccionar otro vendedor.'
    : 'Debe asignar un vendedor a la sesión antes de completar una venta.'

  const openWbs = () => {
    if (!registerContext?.can_complete_sale) {
      toast.error(sellerBlockerMessage)
      if (canAssignSeller && registerContext?.session) setShowChangeSeller(true)
      return
    }

    setShowWbsModal(true)
  }

  // accesibilidad de teclado
  useHotkeys(['f10', 'alt+h'], (e) => {
    e.preventDefault()
    setShowShortcutsHelp(prev => !prev)
  }, { enableOnFormTags: true }, [])

  useHotkeys(['f2', 'alt+b'], (e) => {
    e.preventDefault()
    document.getElementById('product-search-input')?.focus()
  }, { enableOnFormTags: true }, [])

  useHotkeys(['f8', 'alt+p'], (e) => {
    e.preventDefault()
    if (cart.length > 0) document.getElementById('process-payment-button')?.click()
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('esc', (e) => {
    if (!showPaymentModal && !showTicketPreview && !showCashWithdrawal && !showCashDeposit && !showCashCount && !showChangeCashier && !showDeliveryDetailsModal) {
      setSearchTerm('')
    }
  }, { enableOnFormTags: true }, [showPaymentModal, showTicketPreview, showCashWithdrawal, showCashDeposit, showCashCount, showChangeCashier, showDeliveryDetailsModal])

  useHotkeys('alt+s', (e) => {
    e.preventDefault()
    if (saleType === 'delivery') {
      setSaleType('regular')
    } else {
      setSaleType('delivery')
      setShowDeliveryDetailsModal(true)
    }
  }, { enableOnFormTags: true }, [saleType])

  useHotkeys('alt+l', (e) => {
    e.preventDefault()
    if (cart.length > 0) clearCartMutation.mutate()
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('alt+up', (e) => {
    e.preventDefault()
    if (cart.length > 0) {
      const lastItem = cart[cart.length - 1]
      updateQuantity(lastItem.id, 1)
    }
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('alt+down', (e) => {
    e.preventDefault()
    if (cart.length > 0) {
      const lastItem = cart[cart.length - 1]
      updateQuantity(lastItem.id, -1)
    }
  }, { enableOnFormTags: true }, [cart])

  useHotkeys(['alt+delete', 'alt+backspace'], (e) => {
    e.preventDefault()
    if (cart.length > 0) {
      const lastItem = cart[cart.length - 1]
      removeFromCart(lastItem.id)
    }
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('alt+r', (e) => {
    e.preventDefault()
    setShowCashWithdrawal(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('alt+d', (e) => {
    e.preventDefault()
    setShowCashDeposit(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('alt+c', (e) => {
    e.preventDefault()
    setShowCashCount(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('f4', (e) => {
    e.preventDefault()
    setShowPriceCheckModal(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('alt+w', (e) => {
    e.preventDefault()
    openWbs()
  }, { enableOnFormTags: true }, [registerContext, canAssignSeller])

  useHotkeys('alt+x', (e) => {
    e.preventDefault()
    if (registerContext?.cash_register?.id) {
      setShowChangeCashier(true)
    }
  }, { enableOnFormTags: true }, [registerContext])


  useEffect(() => {
    if (!isLoadingContext && registerContext) {
      if (!registerContext.cash_register || !registerContext.cash_register.is_active) {
        setShowRegisterAssignment(true)
        setShowOpenSession(false)
      } else if (!registerContext.session) {
        if (!showCashCountResult && !showChangeCashier) {
          setShowRegisterAssignment(false)
          setShowOpenSession(true)
        }
      } else {
        setShowRegisterAssignment(false)
        setShowOpenSession(false)
        setCurrentCashInRegister(
          parseFloat(registerContext.session.cash_balance_amount) ||
          parseFloat(registerContext.session.opening_cash_amount) ||
          0
        )
      }
    }
  }, [isLoadingContext, registerContext, showCashCountResult, showChangeCashier])

  const addToCart = (product: Product) => {
    addProductToCart(product)
  }

  const addProductToCart = (product: Product) => {
    if (product.requiresLot) {
      openControlledProduct(product)
      return
    }
    addItemMutation.mutate({ product_id: product.id, quantity: 1, productName: product.name })
  }

  const updateQuantity = (id: number, delta: number) => {
    const item = cart.find((i: any) => i.id === id)
    if (!item) return
    if (item.requiresLot) {
      openControlledProduct(item, delta)
    } else {
      quantityUpdates.enqueue(id, delta)
    }
  }

  const removeFromCart = (id: number) => {
    if (quantityUpdates.isProductPending(id)) return
    removeItemMutation.mutate(id)
  }

  const confirmInventorySelection = async ({ quantity, allocations }: InventorySelectionResult) => {
    if (!inventorySelection) return
    try {
      const nextCart = await persistControlledInventorySelection(
        inventorySelection,
        { quantity, allocations },
        posCartService,
      )
      syncCart(nextCart)
      announceProductAdded(inventorySelection.product.name)
    } catch (error) {
      const latestCart = cartFromValidationError(error)
      if (latestCart) {
        syncCart(latestCart)
        const latestItem = latestCart.items.find((item) => item.id === inventorySelection.product.id)
        if (latestItem && inventorySelection.mode === 'update') {
          setInventorySelection((current) => current ? {
            ...current,
            currentQuantity: latestItem.quantity,
            version: latestItem.version,
          } : current)
        }
      }
      throw error
    }

    if (inventorySelection.searchKey) {
      lastAutoAddedSearchRef.current = inventorySelection.searchKey
      autoAddInFlightRef.current = null
      setSearchTerm('')
      window.setTimeout(() => document.getElementById('product-search-input')?.focus(), 0)
    }
    setInventorySelection(null)
  }

  const toggleSelectItem = (id: number) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    )
  }

  const deleteSelectedItems = async () => {
    if (selectedItemIds.length === cart.length) {
      await clearCartMutation.mutateAsync()
    } else {
      for (const id of selectedItemIds) {
        await removeItemMutation.mutateAsync(id)
      }
    }
    setSelectedItemIds([])
  }

  const subtotal = parseFloat(String(cartData?.totals?.subtotal || '0').replace(/[^0-9.-]+/g, ""))
  const discountTotal = parseFloat(String(cartData?.totals?.discount_total || '0').replace(/[^0-9.-]+/g, ""))
  const regularTotal = parseFloat(String(cartData?.totals?.regular_total || '0').replace(/[^0-9.-]+/g, ""))
  const savingsTotal = parseFloat(String(cartData?.totals?.savings_total || '0').replace(/[^0-9.-]+/g, ""))
  const tax = parseFloat(String(cartData?.totals?.tax_total || '0').replace(/[^0-9.-]+/g, ""))
  const deliveryCost = saleType === 'delivery' ? deliveryDetails.deliveryCost : 0
  const totalBeforePoints = parseFloat(String(cartData?.totals?.total || '0').replace(/[^0-9.-]+/g, "")) + deliveryCost

  const {
    data: loyaltyQuote,
    isFetching: isQuotingLoyalty,
    refetch: refetchLoyaltyQuote,
  } = usePosLoyaltyQuote({
    customerId: selectedCustomer,
    cartRevision,
    hasCart: cart.length > 0,
    useLoyalty: usePoints,
    isDelivery: saleType === 'delivery',
  })

  const pointsEarned = loyaltyQuote?.pointsToEarn ?? 0
  const pointsDiscount = loyaltyQuote?.creditApplied ?? 0
  const total = loyaltyQuote?.externalDue ?? totalBeforePoints
  const isFullyCoveredByLoyalty = pointsDiscount > 0 && total === 0

  const openPayment = async () => {
    if (validateCartMutation.isPending || quantityUpdates.isPending) return
    if (!registerContext?.can_complete_sale) {
      toast.error(sellerBlockerMessage)
      if (canAssignSeller && registerContext?.session) setShowChangeSeller(true)
      return
    }

    try {
      const [validatedCart] = await Promise.all([
        validateCartMutation.mutateAsync(),
        refetchContext(),
      ])
      const loyaltyResult = selectedCustomer
        ? await refetchLoyaltyQuote({ throwOnError: true })
        : null
      const validatedTotal = loyaltyResult?.data?.externalDue
        ?? parseFloat(String(validatedCart.totals.total)) + deliveryCost

      if (Math.abs(validatedTotal - total) >= 0.01) {
        toast.info('El carrito fue actualizado con los precios vigentes.')
      }

      if ((loyaltyResult?.data?.creditApplied ?? 0) > 0 && validatedTotal === 0) {
        const technicalPaymentMethod = paymentMethods.find(
          (method) => method.code === 'cash' && method.is_active,
        ) ?? paymentMethods.find((method) => method.is_active)
        setSelectedPaymentMethod(technicalPaymentMethod?.code ?? null)
        setCashReceived('0')
        setSelectedBank(null)
        setReference('')
      }

      if (validatedCart.regulatory?.requires_prescription
        && !validatedCart.regulatory.is_ready_for_checkout) {
        setShowPrescriptionModal(true)
      } else {
        setShowPaymentModal(true)
      }
    } catch {
      // La mutacion sincroniza el carrito invalido y muestra el error.
    }
  }

  const customerPoints = loyaltyQuote?.pointsBalance ?? 0

  const generateTicketNumber = () => {
    let prefix = ''

    if (selectedPaymentMethod === 'transfer' || selectedPaymentMethod === 'card') {
      prefix = `${selectedBank?.name || ''}-`
    } else if (selectedPaymentMethod === 'cash') {
      prefix = 'EF-'
    } else if (selectedPaymentMethod === 'credit') {
      prefix = 'CR-'
    } else {
      prefix = 'VT-'
    }

    return `${prefix}${Math.floor(Math.random() * 9000) + 1000}`
  }

  const proceedToPayment = async () => {
    if (validateCartMutation.isPending || quantityUpdates.isPending) return

    let validatedCart: PosCartData

    try {
      validatedCart = await validateCartMutation.mutateAsync()
    } catch {
      return
    }

    const loyaltyResult = selectedCustomer
      ? await refetchLoyaltyQuote({ throwOnError: true }).catch(() => null)
      : null
    if (selectedCustomer && !loyaltyResult?.data) {
      toast.error('No fue posible actualizar la cotización de lealtad.')
      return
    }
    const validatedTotal = loyaltyResult?.data?.externalDue
      ?? parseFloat(String(validatedCart.totals.total)) + deliveryCost

    if (Math.abs(validatedTotal - total) >= 0.01) {
      toast.info('El total cambio. Revisa los datos del pago antes de continuar.')

      return
    }

    // Refresh before checking because another operation may have changed the
    // balance or an administrator may have edited the register limit.
    if (selectedPaymentMethod === 'cash' && validatedTotal > 0) {
      const contextResult = await refetchContext({ throwOnError: true }).catch(() => null)
      const freshCapacity = contextResult?.data?.cash_capacity

      if (!freshCapacity) {
        toast.error('No fue posible verificar la capacidad actual de la caja. Intente nuevamente.')
        return
      }

      if (!freshCapacity.can_receive_cash) {
        toast.error('La caja ya supera el límite de efectivo. Realice un retiro o seleccione otro método de pago.')
        return
      }

    }

    if (requiresInvoice) {
      if (customerType === 'general') {
        setInvoiceData(prev => ({
          ...prev,
          rfc: 'XAXX010101000',
          name: 'PUBLICO EN GENERAL',
          cfdi_use: 'S01',
          fiscal_regime: '616',
          zip_code: facturamaProfile?.IssuedIn?.ZipCode || facturamaProfile?.TaxAddress?.ZipCode || prev.zip_code || '',
        }))
      } else {
        const c = customers.find((c: any) => c.id === selectedCustomer)
        if (c) {
          setInvoiceData(prev => ({
            ...prev,
            rfc: c.rfc || prev.rfc,
            name: c.name || prev.name,
            cfdi_use: c.cfdi_use || prev.cfdi_use,
            fiscal_regime: c.fiscal_regime || prev.fiscal_regime,
            zip_code: c.zip_code || prev.zip_code,
            email: c.email || prev.email,
          }))
        }
      }
      setShowInvoiceDataModal(true)
    } else {
      const ticket = generateTicketNumber()
      setTicketNumber(ticket)
      setShowPaymentModal(false)
      setShowTicketPreview(true)
    }
  }

  const confirmInvoiceData = () => {
    setShowInvoiceDataModal(false)
    const ticket = generateTicketNumber()
    setTicketNumber(ticket)
    setShowPaymentModal(false)
    setShowTicketPreview(true)
  }

  const resetSaleState = () => {
    setShowSuccessModal(false)
    setSelectedPaymentMethod(null)
    setCashReceived('')
    setSaleType('regular')
    setCustomerType('general')
    setSelectedCustomer(null)
    setSelectedDeliveryPerson('')
    setDeliveryNotes('')
    setRequiresInvoice(false)
    setTicketCopies(1)
    setSelectedBank(null)
    setReference('')
    setDeliveryDetails({
      deliveryUserId: null,
      deliveryPerson: '',
      recipientName: '',
      customerPhone: '',
      customerAddress: '',
      observations: '',
      deliveryCost: 0,
    })
    setUsePoints(false)
    setInvoiceStatus('idle')
    setInvoiceError(null)
    setCfdiPdfBase64(null)
    setInvoiceData({
      rfc: '', name: '', cfdi_use: '', fiscal_regime: '', zip_code: '', email: ''
    })
  }

  const completeSale = async (): Promise<boolean> => {
    const paymentMethod = paymentMethods.find(
      (method) => method.code === selectedPaymentMethod && method.is_active,
    )

    if (!paymentMethod) {
      toast.error('El método de pago seleccionado no está disponible.')

      return false
    }

    const payload: any = {
      idempotency_key: generateUUID(),
      customer_id: selectedCustomer || null,
      requires_invoice: requiresInvoice,
      payment_method_id: paymentMethod.id,
      loyalty_quote_token: selectedCustomer ? loyaltyQuote?.quoteToken : undefined,
    }

    if (selectedPaymentMethod === 'cash') {
      payload.cash_received_amount = isFullyCoveredByLoyalty ? '0' : cashReceived || total.toString()
    } else if (selectedPaymentMethod === 'card' || selectedPaymentMethod === 'credit' || selectedPaymentMethod === 'transfer') {
      if (selectedBank?.name?.trim()) payload.bank_name = selectedBank.name.trim()
      if (selectedBank?.id) payload.bank_id = selectedBank.id
      if (reference.trim()) payload.reference = reference.trim()
    }

    if (requiresInvoice) {
      payload.invoice_receiver = invoiceData
    }

    if (saleType === 'delivery') {
      payload.delivery = {
        delivery_user_id: deliveryDetails.deliveryUserId,
        recipient_name: deliveryDetails.recipientName,
        recipient_phone: deliveryDetails.customerPhone,
        address: deliveryDetails.customerAddress,
        observations: deliveryDetails.observations
      }
    }

    if (requiresInvoice) {
      setInvoiceStatus('loading')
      setInvoiceError(null)
      setCfdiPdfBase64(null)
    }

    try {
      const data: any = await processSaleMutation.mutateAsync(payload)
      setTicketNumber(data?.sale?.ticket_number || data?.ticket_number || generateTicketNumber())
      setCompletedSaleTotal(total)

      queryClient.invalidateQueries({ queryKey: ['pos-last-completed-sale'] })

      if (requiresInvoice) {
        if (data?.invoice_error) {
          setInvoiceStatus('error')
          setInvoiceError(data.invoice_error)
        } else {
          setInvoiceStatus('success')
          if (data?.sale?.cfdi_id) {
            setCfdiPdfBase64(data.sale.cfdi_id)
          }
        }
      }

      return true
    } catch {
      return false
    }
  }

  const finishCompletedSale = () => {
    setShowTicketPreview(false)
    setShowPaymentModal(false)
    setShowSuccessModal(true)
  }

  const handleDownloadPdf = async () => {
    if (!cfdiPdfBase64) return
    try {
      toast.info("Descargando PDF...")
      const base64 = await facturamaService.downloadInvoicePdf(cfdiPdfBase64)
      const linkSource = `data:application/pdf;base64,${base64}`
      const downloadLink = document.createElement("a")
      downloadLink.href = linkSource
      downloadLink.download = `Factura_${ticketNumber}.pdf`
      downloadLink.click()
      toast.success("PDF descargado correctamente")
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error al descargar el PDF")
    }
  }

  const handleDownloadXml = async () => {
    if (!cfdiPdfBase64) return
    try {
      toast.info("Descargando XML...")
      const base64 = await facturamaService.downloadInvoiceXml(cfdiPdfBase64)
      const linkSource = `data:application/xml;base64,${base64}`
      const downloadLink = document.createElement("a")
      downloadLink.href = linkSource
      downloadLink.download = `Factura_${ticketNumber}.xml`
      downloadLink.click()
      toast.success("XML descargado correctamente")
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error al descargar el XML")
    }
  }

  const generateUUID = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  const handleCashWithdrawal = () => {
    const amount = parseFloat(withdrawalAmount)
    if (
      amount > 0 &&
      amount <= currentCashInRegister &&
      withdrawalConcept.trim()
    ) {
      withdrawalMutation.mutate({
        idempotency_key: generateUUID(),
        amount: amount.toString(),
        concept: withdrawalConcept.trim()
      })
    }
  }

  const handleCashDeposit = (target: CashDepositTarget) => {
    const amount = parseFloat(depositAmount)
    if (registerContext?.cash_capacity?.can_receive_cash === false) {
      toast.error('La caja ya supera el límite de efectivo. Realice un retiro antes de registrar otro ingreso.')
      return
    }

    if (amount > 0 && target.type !== 'general') {
      orderDepositMutation.mutate(target)
      return
    }
    if (amount > 0 && depositConcept.trim()) {
      depositMutation.mutate({
        idempotency_key: generateUUID(),
        amount: amount.toString(),
        concept: depositConcept.trim()
      })
    }
  }

  const calculateDenominationsTotal = (denom: typeof denominations) => {
    return Object.entries(denom).reduce((total, [value, quantity]) => {
      return total + parseFloat(value) * quantity
    }, 0)
  }

  const handleDenominationChange = (denomination: number, quantity: number) => {
    const newDenominations = { ...denominations, [denomination]: quantity }
    setDenominations(newDenominations)
    const total = calculateDenominationsTotal(newDenominations)
    setCountedCash(total.toString())
  }

  const handleCashCount = () => {
    // Convert object to array for backend
    const denominationsArray = Object.entries(denominations)
      .filter(([_, quantity]) => quantity > 0)
      .map(([denom, quantity]) => ({
        denomination: parseInt(denom),
        quantity
      }))

    closeSessionMutation.mutate({
      denominations: denominationsArray,
      notes: cashCountNotes.trim() || 'Corte final'
    })
  }

  const closeCashCountResult = () => {
    setShowCashCountResult(false)
    setCountedCash('')
    setCashCountDifference(0)
    setCashCountNotes('')
  }

  const handleReleaseRegister = () => {
    if (!registerContext?.cash_register?.id) return
    releaseMutation.mutate(releasePin)
  }

  const change =
    selectedPaymentMethod === 'cash'
      ? parseFloat(cashReceived || '0') - total
      : 0
  const cashCapacity = registerContext?.cash_capacity ?? registerContext?.session?.cash_capacity ?? null
  const cashWarning = cashCapacity?.status === 'WARNING'
  const cashCritical = cashCapacity?.status === 'CRITICAL'
  const sellerName = [registerContext?.seller?.name, registerContext?.seller?.last_name]
    .filter(Boolean)
    .join(' ') || 'Sin vendedor asignado'

  useHotkeys(['right', 'down', 'left', 'up'], (e) => {
    const grid = document.getElementById('pos-product-grid')
    if (grid && !grid.contains(document.activeElement)) {
      const firstProduct = grid.querySelector('[tabindex="0"]') as HTMLElement
      if (firstProduct) {
        e.preventDefault()
        firstProduct.focus()
      }
    }
  }, { enableOnFormTags: false })

  return (
    <ErrorBoundary>
      <div className="relative flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col overflow-hidden lg:h-full lg:flex-row">
        {/* Left Side - Products & Context */}
        <div className={`min-h-0 flex-col overflow-hidden p-3 sm:p-4 lg:flex lg:flex-1 lg:p-6 ${mobilePanel === 'products' ? 'flex flex-1' : 'flex shrink-0'}`}>
          {/* Header with cashier info */}

          <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <h1 className="shrink-0 text-xl font-semibold leading-tight text-slate-900 xl:text-2xl">
              Punto de Venta
            </h1>
            <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
              {lastSaleId && canAction('pos.sale.print') && (
                <Popover open={showLastSalePopover} onOpenChange={setShowLastSalePopover}>
                  <PopoverTrigger asChild>
                    <button
                      className="rounded-lg border border-primary bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90"
                      title="Reimprimir última venta"
                    >
                      <Printer className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" align="start">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        value={lastSaleCopies || ''}
                        onChange={(e) => {
                          const val = parseInt(e.target.value)
                          setLastSaleCopies(isNaN(val) ? 0 : val)
                        }}
                        onBlur={() => {
                          if (!lastSaleCopies || lastSaleCopies < 1) setLastSaleCopies(1)
                        }}
                        className="h-9 w-16 text-center"
                      />
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          setShowLastSalePopover(false)
                          setShowLastSalePrint(true)
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Printer className="mr-2 h-4 w-4" />
                        Imprimir
                      </Button>
                      <button
                        onClick={() => setShowLastSalePopover(false)}
                        className="rounded-md border border-slate-200 p-2 hover:bg-slate-100"
                      >
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              <div className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 xl:px-4">
                <Monitor className="h-4 w-4 shrink-0 text-slate-600" />

                <span className="min-w-0 truncate text-sm font-medium text-slate-900">
                  {registerContext?.cash_register?.name || 'Caja No Asignada'}
                </span>
                {registerContext?.cash_register?.id && (
                  <button
                    onClick={() => setShowChangeCashier(true)}
                    className="ml-1 shrink-0 rounded p-1 hover:bg-slate-100 xl:ml-2"
                    aria-label="Cambiar cajero"
                    title="Cambiar cajero"
                  >
                    <Key className="h-4 w-4 text-slate-600" />
                  </button>

                )}
              </div>

              {registerContext?.session && (
                <div className={`flex min-w-0 items-center gap-2 rounded-lg border px-3 py-2 xl:px-4 ${
                  registerContext.can_complete_sale
                    ? 'border-slate-200 bg-white'
                    : 'border-yellow-200 bg-yellow-50'
                }`}>
                  <User className={`h-4 w-4 shrink-0 ${registerContext.can_complete_sale ? 'text-slate-600' : 'text-yellow-700'}`} />
                  <span className="min-w-0 truncate text-sm font-medium text-slate-900" title={sellerName}>
                    {sellerName}
                  </span>
                  {canAssignSeller && (
                    <button
                      onClick={() => setShowChangeSeller(true)}
                      className="ml-1 shrink-0 rounded p-1 hover:bg-slate-100 xl:ml-2"
                      aria-label="Cambiar vendedor"
                      title="Cambiar vendedor"
                    >
                      <Users className="h-4 w-4 text-slate-600" />
                    </button>
                  )}
                </div>
              )}

              {/* Cash in register */}
              <div
                className={`rounded-lg border px-4 py-2 ${cashCritical
                  ? 'border-red-200 bg-red-50'
                  : cashWarning
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-border bg-card'
                  }`}
              >
                <div className="flex items-center gap-2">
                  {isLoadingContext ? (
                    <Loader2 className="h-4 w-4 animate-spin text-card-foreground" />
                  ) : (
                    <>
                      <Banknote
                        className={`h-4 w-4 ${cashCritical
                          ? 'text-red-600'
                          : cashWarning
                            ? 'text-yellow-600'
                            : 'text-green-600'
                          }`}
                      />
                      <span className="text-sm font-semibold text-card-foreground">
                        ${currentCashInRegister.toFixed(2)} MXN
                      </span>
                    </>
                  )}
                </div>
              </div>
              

              {canAction('pos.pos.wbs') && (
               <button
  type="button"
  onClick={openWbs}
  className="rounded-lg border border-orange-500 bg-orange-500 p-2 text-white transition-colors hover:bg-orange-500/90"
  title="Recargas y Servicios"
>
  <Zap className="h-4 w-4" />
</button>
              )}

              {canAction('pos.cash_register.open') && (
                <>
                  <button
                    onClick={() => setShowCashDeposit(true)}
                    className="rounded-lg border border-green-600 bg-green-600 p-2 text-white transition-colors hover:bg-green-600/90"
                    aria-label="Ingreso de efectivo"
                    title="Ingreso de efectivo"
                  >
                    <LogIn className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setShowCashWithdrawal(true)}
                    className="rounded-lg border border-red-600 bg-red-600 p-2 text-white transition-colors hover:bg-red-600/90"
                    aria-label="Retiro de efectivo"
                    title="Retiro de efectivo"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setShowCashCount(true)}
                    className="rounded-lg border border-primary bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90"
                    aria-label="Corte de caja"
                    title="Corte de caja"
                  >
                    <DollarSign className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          <PosMobileTabs
            activePanel={mobilePanel}
            cartQuantity={cartQuantity}
            onPanelChange={setMobilePanel}
            onPriceCheck={() => setShowPriceCheckModal(true)}
          />

          <div
            id="pos-products-panel"
            role="tabpanel"
            aria-labelledby="pos-products-tab"
            className={`min-h-0 flex-1 flex-col ${mobilePanel === 'products' ? 'flex' : 'hidden'} lg:flex`}
          >
            {/* Cash limit warning */}
            {(cashWarning || cashCritical) && cashCapacity && (
              <div className={`mb-4 flex items-center gap-2 rounded-lg border p-3 ${cashCritical ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
                <AlertCircle className={`h-5 w-5 shrink-0 ${cashCritical ? 'text-red-600' : 'text-yellow-600'}`} />
                <p className={`text-sm ${cashCritical ? 'text-red-800' : 'text-yellow-800'}`}>
                  {cashCapacity.label}: {cashCapacity.message}
                  {cashCapacity.is_over_limit
                    ? ` Exceso: $${Number(cashCapacity.excess_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN.`
                    : ` Disponible: $${Number(cashCapacity.remaining_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN.`}
                </p>
              </div>
            )}

            <ProductDiscoveryControls
              search={searchTerm}
              useCaseId={selectedUseCase}
              onSearchChange={setSearchTerm}
              onUseCaseChange={setSelectedUseCase}
            />

            {/* Products Grid */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div
                id="pos-product-grid"
                className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
                onKeyDown={(e) => {
                  if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                    const active = document.activeElement as HTMLElement;
                    if (!active || !e.currentTarget.contains(active)) return;
                    e.preventDefault();

                    const items = Array.from(e.currentTarget.children) as HTMLElement[];
                    const index = items.indexOf(active);
                    if (index === -1) return;

                    let cols = 1;
                    if (window.innerWidth >= 1280) cols = 3;
                    else if (window.innerWidth >= 768) cols = 2;

                    let nextIndex = index;
                    if (e.key === 'ArrowRight') nextIndex = Math.min(index + 1, items.length - 1);
                    if (e.key === 'ArrowLeft') nextIndex = Math.max(index - 1, 0);
                    if (e.key === 'ArrowDown') nextIndex = Math.min(index + cols, items.length - 1);
                    if (e.key === 'ArrowUp') nextIndex = Math.max(index - cols, 0);

                    items[nextIndex]?.focus();
                  }
                }}
              >
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    currentSucursalId={registerContext?.sucursal?.id}
                    addToCart={addToCart}
                  />
                ))}
              </div>
              {!isLoadingData && products.length === 0 && (
                <p className="py-12 text-center text-sm text-slate-500">No se encontraron productos.</p>
              )}

              {/* Boton de cargar mas movido al fondo de la lista */}
              {hasNextPage && (
                <div className="py-6 text-center">
                  <Button
                    variant="outline"
                    disabled={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                    className="w-full max-w-xs"
                  >
                    {isFetchingNextPage ? 'Cargando...' : 'Cargar más productos'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Cart */}
        <div
          id="pos-cart-panel"
          role="tabpanel"
          aria-labelledby="pos-cart-tab"
          className={`min-h-0 flex-1 ${
            mobilePanel === 'cart' ? 'flex' : 'hidden'
          } lg:flex lg:flex-none`}
        >
          <PosCart
            saleType={saleType}
            setSaleType={setSaleType}
            deliveryDetails={deliveryDetails}
            setShowDeliveryDetailsModal={setShowDeliveryDetailsModal}
            customerType={customerType}
            setCustomerType={setCustomerType}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            setShowNewCustomerModal={setShowNewCustomerModal}
            customerPoints={customerPoints}
            loyaltyCredit={loyaltyQuote?.creditBalance ?? 0}
            hasAvailableReward={Boolean(loyaltyQuote?.maximumReward)}
            nextReward={loyaltyQuote?.nextReward ?? null}
            pointsToNextReward={loyaltyQuote?.pointsToNextReward ?? null}
            loyaltyEnabled={loyaltyQuote?.programEnabled ?? false}
            isQuotingLoyalty={isQuotingLoyalty}
            pointsEarned={pointsEarned}
            usePoints={usePoints}
            setUsePoints={setUsePoints}
            cart={cart}
            clearCart={() => clearCartMutation.mutate()}
            deleteSelectedItems={deleteSelectedItems}
            toggleSelectItem={toggleSelectItem}
            removeFromCart={removeFromCart}
            updateQuantity={updateQuantity}
            subtotal={subtotal}
            discountTotal={discountTotal}
            regularTotal={regularTotal}
            savingsTotal={savingsTotal}
            tax={tax}
            deliveryCost={deliveryCost}
            pointsDiscount={pointsDiscount}
            total={total}
            onProcessPayment={openPayment}
            isValidatingCart={validateCartMutation.isPending}
            canCompleteSale={Boolean(registerContext?.can_complete_sale)}
            isUpdatingQuantities={quantityUpdates.isPending}
            isProductQuantityPending={quantityUpdates.isProductPending}
            prescriptions={cartData?.prescriptions}
            regulatory={cartData?.regulatory}
            onEditPrescription={() => setShowPrescriptionModal(true)}
          />
        </div>
        {/* Payment Modal */}
        <PaymentModal open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          total={total}
          isFullyCoveredByLoyalty={isFullyCoveredByLoyalty}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          cashReceived={cashReceived}
          setCashReceived={setCashReceived}
          change={change}
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          reference={reference}
          setReference={setReference}
          requiresInvoice={requiresInvoice}
          setRequiresInvoice={setRequiresInvoice}
          ticketCopies={ticketCopies}
          setTicketCopies={setTicketCopies}
          proceedToPayment={proceedToPayment}
          isValidatingCart={validateCartMutation.isPending || quantityUpdates.isPending}
          cashCapacity={cashCapacity} />

        {/* Ticket Preview Modal */}
        {showTicketPreview && <TicketPreviewModal
          open={showTicketPreview}
          onOpenChange={setShowTicketPreview}
          ticketNumber={ticketNumber}
          branchName={registerContext?.sucursal?.name}
          cashRegisterName={registerContext?.cash_register?.name}
          cashierName={sellerName}
          customerType={customerType}
          selectedCustomer={selectedCustomer}
          customers={customers}
          saleType={saleType}
          deliveryDetails={deliveryDetails}
          cart={cart}
          subtotal={subtotal}
          discountTotal={discountTotal}
          savingsTotal={savingsTotal}
          tax={tax}
          deliveryCost={deliveryCost}
          pointsDiscount={pointsDiscount}
          grossTotal={totalBeforePoints}
          total={total}
          pointsEarned={pointsEarned}
          pointsRedeemed={loyaltyQuote?.reward?.pointsRequired ?? 0}
          creditGenerated={loyaltyQuote?.creditGenerated ?? 0}
          creditRemaining={loyaltyQuote?.creditRemaining ?? 0}
          selectedPaymentMethod={selectedPaymentMethod}
          selectedBank={selectedBank?.name || ''}
          reference={reference}
          cashReceived={cashReceived}
          change={change}
          requiresInvoice={requiresInvoice}
          ticketCopies={ticketCopies}
          completeSale={completeSale}
          onCompleted={finishCompletedSale}
          isCompleting={processSaleMutation.isPending}
          setShowPaymentModal={setShowPaymentModal}
        />}

        {/* Success Modal */}
        <SuccessModal open={showSuccessModal}
          onOpenChange={(open) => {
            if (!open) resetSaleState()
          }}
          ticketNumber={ticketNumber}
          total={completedSaleTotal}
          requiresInvoice={requiresInvoice}
          invoiceStatus={invoiceStatus}
          invoiceError={invoiceError}
          cfdiPdfBase64={cfdiPdfBase64}
          onDownloadPdf={handleDownloadPdf}
          onDownloadXml={handleDownloadXml}
          onClose={resetSaleState}
        />

        {/* Cash Withdrawal Modal */}
        <CashWithdrawalModal
          open={showCashWithdrawal}
          onOpenChange={setShowCashWithdrawal}
          currentCashInRegister={currentCashInRegister}
          withdrawalAmount={withdrawalAmount}
          setWithdrawalAmount={setWithdrawalAmount}
          withdrawalConcept={withdrawalConcept}
          setWithdrawalConcept={setWithdrawalConcept}
          isSubmitting={withdrawalMutation.isPending}
          handleCashWithdrawal={handleCashWithdrawal}
          setShowCashWithdrawal={setShowCashWithdrawal}
        />

        {/* Cash Deposit Modal */}
        <CashDepositModal
          open={showCashDeposit}
          onOpenChange={setShowCashDeposit}
          currentCashInRegister={currentCashInRegister}
          depositAmount={depositAmount}
          setDepositAmount={setDepositAmount}
          depositConcept={depositConcept}
          setDepositConcept={setDepositConcept}
          isSubmitting={depositMutation.isPending || orderDepositMutation.isPending}
          handleCashDeposit={handleCashDeposit}
          setShowCashDeposit={setShowCashDeposit}
          cashCapacity={cashCapacity}
          sucursalId={registerContext?.sucursal?.id}
        />

        {/* Cash Count Modal */}
        <CashCountModal
          open={showCashCount}
          onOpenChange={setShowCashCount}
          denominations={denominations}
          handleDenominationChange={handleDenominationChange}
          countedCash={countedCash}
          setCountedCash={setCountedCash}
          handleCashCount={handleCashCount}
          setShowCashCount={setShowCashCount}
          isSubmitting={closeSessionMutation.isPending}
          notes={cashCountNotes}
          setNotes={setCashCountNotes}
          currentCashInRegister={currentCashInRegister}
        />

        {/* Cash Count Result Modal */}
        <CashCountResultModal
          open={showCashCountResult}
          onOpenChange={setShowCashCountResult}
          cashCountStatus={cashCountStatus}
          cashierNumber={registerContext?.cash_register?.id || 1}
          cashierName={[registerContext?.session?.opened_by?.name, registerContext?.session?.opened_by?.last_name].filter(Boolean).join(' ') || 'Admin'}
          currentCashInRegister={currentCashInRegister}
          countedCash={countedCash}
          denominations={denominations}
          cashCountDifference={cashCountDifference}
          sessionData={closedSessionData}
          sucursalName={registerContext?.sucursal?.name}
          closeCashCountResult={() => setShowCashCountResult(false)}
        />

        {/* Change Cashier Modal */}
        <ChangeCashierModal
          open={showChangeCashier}
          onOpenChange={setShowChangeCashier}
          pin={releasePin}
          setPin={setReleasePin}
          handleReleaseRegister={handleReleaseRegister}
          isSubmitting={releaseMutation.isPending}
        />

        <ChangeSellerModal
          open={showChangeSeller}
          onOpenChange={setShowChangeSeller}
          currentSeller={registerContext?.seller ?? null}
        />

        {/* Prescription Modal */}
        <PrescriptionModal open={showPrescriptionModal}
          onOpenChange={setShowPrescriptionModal}
          items={cart}
          prescriptions={cartData?.prescriptions || []}
          onSaved={() => {
            setShowPrescriptionModal(false)
            setShowPaymentModal(true)
          }} />

        {/* New Customer Modal - Quick Registration from POS */}
        <NewCustomerModal open={showNewCustomerModal}
          onOpenChange={setShowNewCustomerModal}
          setShowNewCustomerModal={setShowNewCustomerModal}
          onSuccess={reloadCustomers} />

        {/* Delivery Details Modal */}
        <DeliveryDetailsModal open={showDeliveryDetailsModal}
          onOpenChange={setShowDeliveryDetailsModal}
          deliveryDetails={deliveryDetails}
          setDeliveryDetails={setDeliveryDetails}

          setShowDeliveryDetailsModal={setShowDeliveryDetailsModal} />

        {/* Register Modals */}
        <RegisterAssignmentModal
          open={showRegisterAssignment}
          onSuccess={() => refetchContext()}
        />
        <OpenRegisterSessionModal
          open={showOpenSession}
          onOpenChange={setShowOpenSession}
          onReleaseRegister={() => {
            setShowOpenSession(false)
            setShowChangeCashier(true)
          }}
          onSuccess={() => refetchContext()}
          cashCapacity={cashCapacity}
        />

        <InvoiceDataModal
          open={showInvoiceDataModal}
          onOpenChange={setShowInvoiceDataModal}
          data={invoiceData}
          setData={setInvoiceData}
          onConfirm={confirmInvoiceData}
        />
        <PriceCheckModal
          isOpen={showPriceCheckModal}
          onClose={() => setShowPriceCheckModal(false)}
          currentSucursalId={registerContext?.sucursal?.id}
          onAddToCart={(product) => addToCart(product)}
          cartQuantity={cartQuantity}
          addedProductNotice={addedProductNotice}
        />

        {/* WBS Recharge Modal */}
        <WbsRechargeModal
          open={showWbsModal}
          onOpenChange={setShowWbsModal}
        />

        <InventorySourceModal
          open={Boolean(inventorySelection)}
          product={inventorySelection?.product ?? null}
          quantity={inventorySelection?.quantity ?? 1}
          quantityMode="derived"
          currentQuantity={inventorySelection?.currentQuantity}
          onOpenChange={(open) => {
            if (!open) {
              autoAddInFlightRef.current = null
              setInventorySelection(null)
            }
          }}
          onConfirm={confirmInventorySelection}
        />

        <div className="absolute bottom-4 left-4 z-50 hidden lg:block">
          <ShortcutsWidget
            expanded={showShortcutsHelp}
            setExpanded={setShowShortcutsHelp}
            onPriceCheck={() => setShowPriceCheckModal(true)}
          />
        </div>

        {/* Last Sale Print Modal */}
        <SaleDetailModal
          isOpen={showLastSalePrint}
          onClose={() => setShowLastSalePrint(false)}
          sale={lastSaleId ? { id: lastSaleId } : null}
          onOpenReturnModal={() => { }}
          onOpenCancelModal={() => { }}
          autoPrint={true}
          headless={true}
          initialTicketCopies={lastSaleCopies}
        />
      </div >
    </ErrorBoundary >
  )
}
